import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import { getArticleSchema, getBreadcrumbSchema } from '../utils/structuredData';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, Clock, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/blog/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error('Failed to fetch post:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center">Loading...</div>;
  }

  if (!post) {
    return <div className="flex min-h-screen items-center justify-center">Post not found</div>;
  }

  // SEO and Structured Data
  const baseUrl = window.location.origin;
  const articleSchema = getArticleSchema(post);
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Home', url: baseUrl },
    { name: 'Blog', url: `${baseUrl}/blog` },
    { name: post.title, url: `${baseUrl}/blog/${post.slug}` }
  ]);

  return (
    <>
      <SEO 
        title={post.seo_meta_title || post.title}
        description={post.seo_meta_description || post.subtitle}
        keywords={post.category}
        image={post.image_url}
        url={`/blog/${post.slug}`}
        type="article"
        article={{
          publishedTime: post.published_date,
          author: post.author,
          tags: [post.category]
        }}
        structuredData={[articleSchema, breadcrumbSchema]}
      />
      <div className="page-container min-h-screen py-12 bg-gradient-to-br from-white via-blue-50 to-mint-50">
      <div className="container mx-auto px-4">
        <Button
          variant="ghost"
          className="mb-8"
          onClick={() => navigate('/blog')}
          data-testid="back-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {language === 'ro' ? 'Înapoi la Blog' : 'Back to Blog'}
        </Button>

        <article className="mx-auto max-w-3xl">
          {/* Header */}
          <div className="mb-8">
            <Badge className="mb-4 bg-teal-600">{post.category}</Badge>
            <h1 className="mb-4 text-4xl font-bold leading-tight text-slate-900 sm:text-5xl" data-testid="post-title">
              {post.title}
            </h1>
            {post.subtitle && (
              <p className="mb-6 text-xl text-slate-600">{post.subtitle}</p>
            )}
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
              <div className="flex items-center">
                <User className="mr-2 h-4 w-4" />
                {post.author}
              </div>
              <div>{new Date(post.published_date).toLocaleDateString()}</div>
            </div>
          </div>

          {/* Featured Image */}
          {post.image_url && (
            <img
              src={post.image_url}
              alt={post.title}
              className="mb-12 w-full rounded-2xl shadow-xl"
            />
          )}

          {/* Content */}
          <div className="prose prose-lg prose-slate max-w-none" data-testid="post-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
          </div>

          {/* CTA */}
          <div className="mt-12 rounded-2xl bg-gradient-to-br from-teal-50 to-slate-50 p-8 text-center">
            <h3 className="mb-4 text-2xl font-bold text-slate-900">
              {language === 'ro'
                ? 'Vrei mai multe resurse?'
                : 'Want more resources?'}
            </h3>
            <p className="mb-6 text-slate-600">
              {language === 'ro'
                ? 'Descarcă pachetul nostru gratuit de start pentru libertatea financiară.'
                : 'Download our free financial freedom starter pack.'}
            </p>
            <Button
              className="bg-teal-600 hover:bg-teal-700"
              onClick={() => navigate('/products/pachet-de-pornire-spre-libertatea-financiara')}
            >
              {language === 'ro' ? 'Descarcă acum' : 'Download now'}
            </Button>
          </div>
        </article>
      </div>
    </div>
    </>
  );
};

export default BlogPost;